import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Crown, Sparkles, User as UserIcon, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import {
  TierId,
  DurationId,
  TIERS,
  DURATIONS,
  getPrice,
  getMonthlyDiscountPercent,
} from '../../constants/subscriptionPlans';
import { formatCompactNumber } from '../../utils/formatNumber';

const LLM_URL = import.meta.env.VITE_LLM_URL || 'https://llm.etherworks.cn';

type AllTierId = 'free' | TierId;

const ALL_TIER_ICONS: Record<AllTierId, React.ReactNode> = {
  free: <UserIcon className="h-5 w-5" />,
  plus: <Sparkles className="h-5 w-5" />,
  max: <Shield className="h-5 w-5" />,
  ultra: <Crown className="h-5 w-5" />,
};

const ALL_TIER_EXTRA_FEATURES: Record<AllTierId, string[]> = {
  free: ['community_support'],
  plus: ['priority_support'],
  max: ['extended_context', 'tools_calling', 'priority_support'],
  ultra: ['priority', 'max_context', 'tools_calling', 'dedicated_support'],
};

const ALL_TIER_STYLES: Record<AllTierId, { gradient: string; border: string; activeBorder: string; badge: string; buttonInactive: string; checkmark: string }> = {
  free: {
    gradient: 'from-card to-muted/20',
    border: 'border-border hover:border-foreground/20',
    activeBorder: 'border-primary ring-1 ring-primary',
    badge: 'bg-muted text-foreground',
    buttonInactive: 'bg-background text-foreground border border-input hover:bg-accent hover:text-accent-foreground',
    checkmark: 'text-foreground/70',
  },
  plus: {
    gradient: 'from-card to-sky-500/5',
    border: 'border-border hover:border-sky-500/30',
    activeBorder: 'border-sky-500 ring-1 ring-sky-500 shadow-lg shadow-sky-500/10',
    badge: 'bg-sky-500/10 text-sky-500',
    buttonInactive: 'bg-sky-500 text-white hover:bg-sky-600 shadow-sm shadow-sky-500/20',
    checkmark: 'text-sky-500',
  },
  max: {
    gradient: 'from-card to-violet-500/5',
    border: 'border-border hover:border-violet-500/30',
    activeBorder: 'border-violet-500 ring-1 ring-violet-500 shadow-lg shadow-violet-500/10',
    badge: 'bg-violet-500/10 text-violet-500',
    buttonInactive: 'bg-violet-500 text-white hover:bg-violet-600 shadow-sm shadow-violet-500/20',
    checkmark: 'text-violet-500',
  },
  ultra: {
    gradient: 'from-card to-amber-500/5',
    border: 'border-border hover:border-amber-500/30',
    activeBorder: 'border-amber-500 ring-1 ring-amber-500',
    badge: 'bg-amber-500/10 text-amber-500',
    buttonInactive: 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20',
    checkmark: 'text-amber-500',
  },
};

export default function LlmPlans() {
  const { user } = useAuth();
  const { t } = useTranslation('plans');
  const [duration, setDuration] = useState<DurationId>('monthly');

  const currencySymbol = '$';

  const activeTier: AllTierId | null = (() => {
    if (!user) return null;
    if (!user.subscription || user.subscription.status !== 'active') return 'free';
    return (user.subscription.tier as TierId) || 'free';
  })();

  return (
    <>
      {/* Duration toggle */}
      <section className="px-4 sm:px-6 lg:px-8 pt-4 pb-12">
        <div className="max-w-xs mx-auto flex items-center bg-muted/50 border border-border rounded-lg p-1">
          {DURATIONS.map(d => (
            <button
              key={d.id}
              onClick={() => setDuration(d.id)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                duration === d.id
                  ? 'bg-background text-foreground shadow-sm border border-border/50'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {t('duration_' + d.id)}
            </button>
          ))}
        </div>
      </section>

      {/* Tier cards */}
      <section className="px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {/* Free card */}
          {(() => {
            const isFreeActive = activeTier === 'free';
            return (
              <div className={`relative flex flex-col rounded-2xl border ${isFreeActive ? ALL_TIER_STYLES.free.activeBorder : ALL_TIER_STYLES.free.border} bg-gradient-to-br ${ALL_TIER_STYLES.free.gradient} p-8 transition-all duration-300 hover:-translate-y-1`}>
                {isFreeActive && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold tracking-wide uppercase flex items-center gap-1">
                    <Check className="h-3.5 w-3.5" /> {t('current_plan')}
                  </div>
                )}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold tracking-tight">{t('free_label')}</h3>
                    <div className={`p-2 rounded-lg ${ALL_TIER_STYLES.free.badge}`}>
                      {ALL_TIER_ICONS.free}
                    </div>
                  </div>
                </div>

                <div className="mb-8 pb-8 border-b border-border/50">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold tracking-tighter">{currencySymbol}0</span>
                    <span className="text-muted-foreground font-medium">{t('forever')}</span>
                  </div>
                </div>

                <div className="mb-6">
                  {(() => {
                    const parts = t('tier_desc_free').split(/\s*[:：、,]\s*/).filter(Boolean);
                    const [category, ...models] = parts;
                    return (
                      <div className="rounded-lg border border-border/60 p-3.5">
                        <p className="text-sm font-semibold text-foreground mb-2">{category}</p>
                        <ul className="space-y-1.5">
                          {models.map((m, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>

                <ul className="space-y-4 text-sm text-muted-foreground flex-1 mb-8">
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${ALL_TIER_STYLES.free.checkmark} shrink-0`} />
                    <span>{t('messages_window', { count: 10, hours: 2 })}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${ALL_TIER_STYLES.free.checkmark} shrink-0`} />
                    <span>{t('tokens_window', { tokens: '200K', hours: 2 })}</span>
                  </li>
                  {ALL_TIER_EXTRA_FEATURES.free.map(key => (
                    <li key={key} className="flex items-start gap-3">
                      <Check className={`h-5 w-5 ${ALL_TIER_STYLES.free.checkmark} shrink-0`} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`${LLM_URL}/console`}
                  className={`w-full h-12 inline-flex items-center justify-center rounded-md font-medium transition-all ${
                    isFreeActive
                      ? 'bg-muted text-muted-foreground cursor-default border border-border'
                      : ALL_TIER_STYLES.free.buttonInactive
                  }`}
                >
                  {isFreeActive ? t('current_plan') : t('get_started')}
                </a>
              </div>
            );
          })()}

          {/* Paid tier cards */}
          {TIERS.map(tier => {
            const price = getPrice(tier.tier, duration);
            const discount = getMonthlyDiscountPercent(tier.tier, duration);
            const isActive = activeTier === tier.tier;
            const isPopular = tier.highlight;
            const tierStyle = ALL_TIER_STYLES[tier.tier];

            return (
              <div
                key={tier.tier}
                className={`relative flex flex-col rounded-2xl border bg-gradient-to-br ${tierStyle.gradient} p-8 transition-all duration-300 hover:-translate-y-1 ${
                  isActive ? tierStyle.activeBorder : isPopular ? tierStyle.activeBorder : tierStyle.border
                }`}
              >
                {isActive && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold tracking-wide uppercase flex items-center gap-1">
                    <Check className="h-3.5 w-3.5" /> {t('current_plan')}
                  </div>
                )}

                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-2xl font-bold tracking-tight">{tier.label}</h3>
                    <div className={`p-2 rounded-lg ${tierStyle.badge}`}>
                      {ALL_TIER_ICONS[tier.tier]}
                    </div>
                  </div>
                </div>

                <div className="mb-8 pb-8 border-b border-border/50">
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-extrabold tracking-tighter">{currencySymbol}{price}</span>
                    <span className="text-muted-foreground font-medium">/{t('per_duration_' + duration)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="mt-2.5">
                      <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-600 px-2.5 py-1 text-xs font-semibold">
                        {t('save_percent', { percent: discount })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mb-6">
                  {(() => {
                    const parts = t('tier_desc_' + tier.tier).split(/\s*[:：、,]\s*/).filter(Boolean);
                    const [category, ...models] = parts;
                    return (
                      <div className="rounded-lg border border-border/60 p-3.5">
                        <p className="text-sm font-semibold text-foreground mb-2">{category}</p>
                        <ul className="space-y-1.5">
                          {models.map((m, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                              <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
                              {m}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })()}
                </div>

                <ul className="space-y-4 text-sm text-muted-foreground flex-1 mb-8">
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${tierStyle.checkmark} shrink-0`} />
                    <span>{t('messages_window', { count: tier.usageLimit, hours: tier.windowHours })}</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className={`h-5 w-5 ${tierStyle.checkmark} shrink-0`} />
                    <span>{t('tokens_window', { tokens: formatCompactNumber(tier.tokenLimit), hours: tier.windowHours })}</span>
                  </li>
                  {ALL_TIER_EXTRA_FEATURES[tier.tier].map(key => (
                    <li key={key} className="flex items-start gap-3">
                      <Check className={`h-5 w-5 ${tierStyle.checkmark} shrink-0`} />
                      <span>{t(key)}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={`${LLM_URL}/settings/subscription`}
                  className={`w-full h-12 inline-flex items-center justify-center rounded-md font-medium transition-all ${
                    isActive
                      ? 'bg-muted text-muted-foreground cursor-default border border-border'
                      : tierStyle.buttonInactive
                  }`}
                >
                  {isActive ? t('current_plan') : t('subscribe_on_llm')}
                </a>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
